import { XMLParser } from 'fast-xml-parser';

import type {
	ConferenceEndpoint,
	ConferenceInfo,
	ConferenceInfoState,
	ConferenceMedia,
	ConferenceUser,
	RawConferenceInfo,
	RawEndpoint,
	RawMedia,
	RawText,
	RawUser,
	TStatus,
} from '../model/types';

/**
 * Применяет partial/full conference-info XML к предыдущему снапшоту.
 *
 * @param prev Предыдущее состояние (может быть null).
 * @param xml Тело NOTIFY с conference-info.
 * @param fallbackEntity URI конференции, если в XML нет entity.
 * @returns Обновлённый snapshot или null, если парсинг не удался.
 */
export const applyConferenceInfoPatch = (
	prev: ConferenceInfo | null,
	xml: string,
	fallbackEntity?: string
): ConferenceInfo | null => {
	const parsed = parseConferenceInfo(xml);
	if (!parsed) return prev;

	const entity = parsed.entity || prev?.entity || fallbackEntity || '';
	const version = parsed.version || prev?.version || 0;

	// Если пришёл full или нет предыдущего состояния — возвращаем как есть.
	if (parsed.state === 'full' || !prev) {
		return { ...parsed, entity, version };
	}

	// Игнорируем старые partial (меньшая или равная версия)
	if (prev.version && parsed.version && parsed.version <= prev.version) {
		return prev;
	}

	return {
		entity,
		state: parsed.state,
		version,
		userCount: parsed.userCount ?? prev.userCount,
		users: mergeUsers(prev.users, parsed.users),
		rawXml: parsed.rawXml ?? prev.rawXml,
	};
};

/** Значение заголовка Accept для Event: conference. */
export const CONFERENCE_ACCEPT = 'application/conference-info+xml';

/** Имя event-package конференции. */
export const CONFERENCE_EVENT = 'conference';

/**
 * Парсит тело NOTIFY с conference-info в удобную структуру.
 *
 * @param xml Исходный XML (может быть null/undefined).
 * @returns Объект ConferenceInfo или null, если парсинг не удался.
 */
export const parseConferenceInfo = (xml?: string | null): ConferenceInfo | null => {
	if (!xml) return null;

	let parsed: {
		['conference-info']?: RawConferenceInfo;
		conferenceInfo?: RawConferenceInfo;
	} | null = null;
	try {
		parsed = xmlParser.parse(xml);
	} catch {
		return null;
	}

	const root = parsed?.['conference-info'] ?? parsed?.conferenceInfo;
	if (!root) return null;

	const users = toArray(root.users?.user)
		.map((user) => parseUser(user))
		.filter((user): user is ConferenceUser => isDefined(user));

	const confState = root['conference-state'] ?? root.conferenceState;

	return {
		entity: readText(root.entity) ?? '',
		state: normalizeState(root.state),
		version: readNumber(root.version) ?? 0,
		userCount: readNumber(confState?.['user-count']),
		users,
		rawXml: xml,
	};
};

const xmlParser = new XMLParser({
	ignoreAttributes: false,
	attributeNamePrefix: '',
	textNodeName: '#text',
	trimValues: true,
	isArray: (_name: string, jpath: string) =>
		jpath === 'conference-info.users.user' ||
		jpath === 'conference-info.users.user.endpoint' ||
		jpath === 'conference-info.users.user.endpoint.media',
});

/**
 * Нормализует поле, которое может прийти как одиночный объект или как массив.
 */
const toArray = <T>(value?: T | T[] | null): T[] => {
	if (!value) return [];
	return Array.isArray(value) ? value : [value];
};

/**
 * Type guard для отбрасывания null/undefined в цепочках map/filter.
 */
const isDefined = <T>(value: T | null | undefined): value is T => value != null;

/**
 * Читает текст из XML-узла, поддерживая строки, числа и объекты вида `{ '#text': ... }`.
 */
const readText = (value?: RawText): string | undefined => {
	if (value == null) return undefined;
	if (typeof value === 'string') {
		const trimmed = value.trim();
		return trimmed || undefined;
	}
	if (typeof value === 'number') return String(value);
	if (typeof value === 'object' && '#text' in value) {
		return readText((value as { '#text'?: RawText })['#text']);
	}
	return undefined;
};

/**
 * Преобразует XML-текст в число, если значение валидно.
 */
const readNumber = (value?: RawText): number | undefined => {
	const raw = readText(value);
	if (!raw) return undefined;

	const parsed = Number(raw);
	return Number.isFinite(parsed) ? parsed : undefined;
};

/**
 * Приводит state узла conference-info к `full`/`partial`.
 */
const normalizeState = (value?: RawText): ConferenceInfoState => {
	const raw = typeof value === 'string' ? value : (readText(value) ?? '');
	return raw.toLowerCase() === 'partial' ? 'partial' : 'full';
};

/**
 * Нормализует статус подключения endpoint в доменный тип.
 */
const toConnectionStatus = (value?: RawText): TStatus | undefined => {
	const raw = typeof value === 'string' ? value : readText(value);
	if (!raw) return undefined;

	const normalized = raw.toLowerCase();
	if (normalized === 'connected') return 'connected';
	if (normalized === 'disconnected') return 'disconnected';
	if (normalized === 'pending') return 'pending';

	return undefined;
};

/**
 * Парсит блок `<media>` участника конференции.
 */
const parseMedia = (raw?: RawMedia): ConferenceMedia | null => {
	if (!raw) return null;

	const type = readText(raw.type);
	if (!type) return null;

	return {
		id: readText(raw.id),
		type,
		status: readText(raw.status) ?? readText(raw.state),
	};
};

/**
 * Парсит блок `<endpoint>` участника конференции.
 */
const parseEndpoint = (raw?: RawEndpoint): ConferenceEndpoint | null => {
	if (!raw) return null;

	const entity = readText(raw.entity);
	if (!entity) return null;

	const media = toArray(raw.media)
		.map((mediaItem) => parseMedia(mediaItem))
		.filter((item): item is ConferenceMedia => isDefined(item));

	return {
		entity,
		status: toConnectionStatus(raw.status),
		media,
	};
};

/**
 * Определяет статус пользователя по его основному endpoint.
 */
const resolveUserStatus = (endpoint: ConferenceEndpoint | null): TStatus | undefined =>
	endpoint?.status;

/**
 * Преобразует XML-узел `<user>` в структуру ConferenceUser.
 */
const parseUser = (raw?: RawUser): ConferenceUser | null => {
	if (!raw) return null;

	const entity = readText(raw.entity);
	if (!entity) return null;

	const displayName = readText(raw['display-text'] ?? raw.displayText);
	const rawEndpoints = toArray(raw.endpoint);
	const primaryEndpoint = rawEndpoints.length > 0 ? parseEndpoint(rawEndpoints[0]) : null;

	const status = resolveUserStatus(primaryEndpoint) as TStatus;
	return {
		id: entity,
		state: normalizeState(raw.state),
		status,
		name: displayName || entity,
		endpoints: primaryEndpoint ? [primaryEndpoint] : [],
	};
};

/**
 * Мержит списки media по ключу `id` (или `type`, если id отсутствует).
 */
const mergeMedia = (prev: ConferenceMedia[], next: ConferenceMedia[]): ConferenceMedia[] => {
	if (next.length === 0) return prev;

	const map = new Map<string, ConferenceMedia>();

	prev.forEach((m) => {
		const key = m.id ?? m.type;
		map.set(key, m);
	});

	next.forEach((m) => {
		const key = m.id ?? m.type;
		const existing = map.get(key);
		if (existing) {
			map.set(key, {
				...existing,
				...m,
				status: m.status ?? existing.status,
			});
		} else {
			map.set(key, m);
		}
	});

	return [...map.values()];
};

/**
 * Мержит endpoint-списки по `entity`, обновляя статус и медиа-подмассив.
 */
const mergeEndpoints = (
	prev: ConferenceEndpoint[],
	next: ConferenceEndpoint[]
): ConferenceEndpoint[] => {
	if (next.length === 0) return prev;

	const map = new Map<string, ConferenceEndpoint>();

	prev.forEach((endpoint) => map.set(endpoint.entity, endpoint));

	next.forEach((endpoint) => {
		const existing = map.get(endpoint.entity);
		if (existing) {
			map.set(endpoint.entity, {
				...existing,
				...endpoint,
				status: endpoint.status ?? existing.status,
				media: mergeMedia(existing.media, endpoint.media),
			});
		} else {
			map.set(endpoint.entity, endpoint);
		}
	});

	return [...map.values()];
};

/**
 * Мержит пользователей conference-info по `id`.
 * Используется для применения partial NOTIFY поверх существующего snapshot.
 */
const mergeUsers = (prev: ConferenceUser[], next: ConferenceUser[]): ConferenceUser[] => {
	if (next.length === 0) return prev;

	const map = new Map<string, ConferenceUser>();

	prev.forEach((user) => map.set(user.id, user));

	next.forEach((user) => {
		const existing = map.get(user.id);
		if (existing) {
			const endpoints = mergeEndpoints(existing.endpoints, user.endpoints);
			map.set(user.id, {
				...existing,
				...user,
				status: user.status ?? existing.status ?? endpoints[0]?.status,
				endpoints,
			});
		} else {
			const endpoints = mergeEndpoints([], user.endpoints);
			map.set(user.id, {
				...user,
				status: user.status ?? endpoints[0]?.status ?? 'pending',
				endpoints,
			});
		}
	});

	return [...map.values()];
};

/**
 * Нормализует значение SIP Contact до URI без параметров.
 *
 * @param value Сырые данные Contact-заголовка.
 * @returns URI без параметров или null, если извлечь URI не удалось.
 */
const normalizeContactUri = (value?: string | null): string | null => {
	if (!value) return null;

	const trimmed = value.trim();
	if (!trimmed) return null;

	const match = trimmed.match(/<\s*([^>]+)\s*>/);
	const rawUri = (match ? match[1] : trimmed).trim();
	if (!rawUri) return null;

	const noParams = rawUri.split(';')[0]?.trim();
	if (!noParams) return null;

	return noParams.replace(/:443\b/, '') || null;
};

/**
 * Извлекает URI и признак конференции (`isfocus`) из заголовка Contact SIP-ответа.
 *
 * @param response SIP-ответ (JsSIP IncomingResponse или сырой объект).
 * @returns Объект с URI контакта и флагом конференции.
 */
export const parseContactUriFromResponse = (
	response: any
): { uri: string | null; isConference: boolean } => {
	const rawContact = response?.getHeader?.('contact');
	const raw = String((Array.isArray(rawContact) ? rawContact[0] : rawContact) || '').trim();
	if (!raw) return { uri: null, isConference: false };

	return {
		uri: normalizeContactUri(raw),
		isConference: raw.toLowerCase().includes('isfocus'),
	};
};
